import { ReactNode } from 'react';

import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  TypographyProps,
} from '@mui/material';
import clsx from 'clsx';

import { tokens } from '../../../theme/generated/tokens';
import { SxProps } from '../../../types';
import { mergeSx } from '../../../utils';

import { statusChipClassNames } from './classNames';
import { StatusChipVariant, StatusChipSize, StatusChipColor } from './types';

type StatusColorConfig = Record<StatusChipVariant, { bg: string; fg: string }>;

const statusColorMap: Record<StatusChipColor, StatusColorConfig> = {
  success: {
    subtle: {
      bg: tokens.theme.bg.status.successSubtle,
      fg: tokens.theme.fg.status.onSuccessSubtle,
    },
    strong: {
      bg: tokens.theme.bg.status.successStrong,
      fg: tokens.theme.fg.status.onSuccessStrong,
    },
  },
  info: {
    subtle: {
      bg: tokens.theme.bg.status.infoSubtle,
      fg: tokens.theme.fg.status.onInfoSubtle,
    },
    strong: {
      bg: tokens.theme.bg.status.infoStrong,
      fg: tokens.theme.fg.status.onInfoStrong,
    },
  },
  neutral: {
    subtle: {
      bg: tokens.theme.bg.status.neutralSubtle,
      fg: tokens.theme.fg.status.onNeutralSubtle,
    },
    strong: {
      bg: tokens.theme.bg.status.neutralStrong,
      fg: tokens.theme.fg.status.onNeutralStrongInverse,
    },
  },
  warning: {
    subtle: {
      bg: tokens.theme.bg.status.warningSubtle,
      fg: tokens.theme.fg.status.onWarningSubtle,
    },
    strong: {
      bg: tokens.theme.bg.status.warningStrong,
      fg: tokens.theme.fg.status.onWarningStrong,
    },
  },
  error: {
    subtle: {
      bg: tokens.theme.bg.status.errorSubtle,
      fg: tokens.theme.fg.status.onErrorSubtle,
    },
    strong: {
      bg: tokens.theme.bg.status.errorStrong,
      fg: tokens.theme.fg.status.onErrorStrong,
    },
  },
};

type SizeConfig = {
  chipHeight: number;
  chipPx: number;
  iconGap: number;
  iconSize: number;
  labelVariant: TypographyProps['variant'];
};

const sizeConfigMap: Record<StatusChipSize, SizeConfig> = {
  small: {
    chipHeight: 20,
    chipPx: 8,
    iconGap: 4,
    iconSize: 12,
    labelVariant: 'P10M',
  },
  medium: {
    chipHeight: 28,
    chipPx: 8,
    iconGap: 6,
    iconSize: 16,
    labelVariant: 'P12M',
  },
  large: {
    chipHeight: 32,
    chipPx: 10,
    iconGap: 4,
    iconSize: 20,
    labelVariant: 'P14M',
  },
};

export type StatusChipProps = {
  /** Primary label text. Omit for an icon-only chip. */
  label?: string;
  color?: StatusChipColor;
  size?: StatusChipSize;
  variant?: StatusChipVariant;
  caption?: string;
  /** Show loading spinner instead of icon */
  loading?: boolean;
  slots?: {
    icon?: ReactNode;
  };
  /** Additional CSS class */
  className?: string;
  /** `data-testid` attribute value */
  testId?: string;
  /** MUI sx prop */
  sx?: SxProps;
};

export const StatusChip = ({
  label,
  color = 'neutral',
  size = 'medium',
  variant = 'subtle',
  caption,
  loading = false,
  slots,
  className,
  testId,
  sx,
  ...rest
}: StatusChipProps) => {
  const { bg, fg } = statusColorMap[color][variant];
  const sizeConfig = sizeConfigMap[size];

  return (
    <Stack
      direction="row"
      alignItems="center"
      className={clsx(
        statusChipClassNames.root,
        statusChipClassNames[color],
        statusChipClassNames[size],
        statusChipClassNames[variant],
        className
      )}
      data-testid={testId}
      gap={sizeConfig.iconGap}
      sx={mergeSx(
        {
          display: 'inline-flex',
          height: sizeConfig.chipHeight,
          px: sizeConfig.chipPx,
          borderRadius: tokens.theme.radius.sm,
          backgroundColor: bg,
          color: fg,
          flexShrink: 0,
          width: 'fit-content',
        },
        sx
      )}
      {...rest}
    >
      {(slots?.icon || loading) && (
        <Box
          className={statusChipClassNames.icon}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: sizeConfig.iconSize,
            height: sizeConfig.iconSize,
            flexShrink: 0,
            '& svg': {
              width: sizeConfig.iconSize,
              height: sizeConfig.iconSize,
            },
          }}
        >
          {loading ? (
            <CircularProgress
              size={sizeConfig.iconSize}
              color="inherit"
              className={statusChipClassNames.spinner}
            />
          ) : (
            slots?.icon
          )}
        </Box>
      )}

      {!!label && (
        <Stack className={statusChipClassNames.labelGroup}>
          {label && (
            <Typography
              className={statusChipClassNames.label}
              color="inherit"
              variant={sizeConfig.labelVariant}
            >
              {label}
            </Typography>
          )}

          {caption && (
            <Typography
              className={statusChipClassNames.caption}
              color="inherit"
              variant="L8M"
              sx={{
                mt: -4,
              }}
            >
              {caption}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
};
